import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ActivityTypeService } from '../activity-type.service';
import { ActivityType } from '../activity-type.model';
import { CardModule } from 'primeng/card';

@Component({
  selector: 'app-edit-activityType',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    IftaLabelModule,
    ToastModule,
    CardModule
  ],
  templateUrl: './edit-activity-type.component.html',
  styleUrl: './edit-activity-type.component.css',
  providers: [MessageService]
})
export class EditActivityTypeComponent implements OnInit {
  activityTypeId: number | null = null;
  activityTypeForm!: FormGroup;
  isEditMode = false;
  pageTitle = 'Create New ActivityType';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private activityTypeService: ActivityTypeService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.activityTypeId = +id;
        this.isEditMode = true;
        this.pageTitle = 'Edit ActivityType';
        this.loadActivityTypeData(this.activityTypeId); 
      }
    });
  }

  initForm(): void {
    this.activityTypeForm = this.fb.group({
      name: ['', Validators.required],
      code: [''],
      arAccount: [''],
      apAccount: [''],
      reference: [''],
      apFuture1: [''],
      apFuture2: [''],
      arFuture1: [''],
      arFuture2: [''],
    });
  }

  loadActivityTypeData(id: number): void {
    this.activityTypeService.getActivityTypeById(id).subscribe({
      next: (activityType) => this.activityTypeForm.patchValue(activityType),
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load activityType data.' });
        console.error(err);
      }
    });
  }

  saveActivityType(): void {
    if (this.activityTypeForm.invalid) {
      this.activityTypeForm.markAllAsTouched();
      return;
    }

    const formValue = this.activityTypeForm.value;
    const activityType: Partial<ActivityType> = this.isEditMode
      ? {
          id: this.activityTypeId ?? undefined,
          ...formValue,
        }
      : {
          ...formValue,
        };

    const operation = this.isEditMode
      ? this.activityTypeService.updateActivityType(activityType)
      : this.activityTypeService.createActivityType(activityType);

    operation.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Activity Type ${this.isEditMode ? 'updated' : 'created'} successfully.`,
        });
        setTimeout(() => this.router.navigate(['/ai-studio/session/current', { outlets: { canvasOutlet: ['activity-types', 'list'] } }]), 1500);
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${this.isEditMode ? 'update' : 'create'} activity type.`,
        });
        console.error(err);
      },
    });
  }

  cancel(): void {
    this.router.navigate(['/ai-studio/session/current', { outlets: { canvasOutlet: ['activity-types', 'list'] } }]);
  }
}
